import React, { useState, useEffect } from 'react';

const AvailabilitySchedule = ({ entityId, onSuccess, onCancel }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const daysOfWeek = [
    { key: 'sunday', label: 'الأحد' },
    { key: 'monday', label: 'الإثنين' },
    { key: 'tuesday', label: 'الثلاثاء' },
    { key: 'wednesday', label: 'الأربعاء' },
    { key: 'thursday', label: 'الخميس' },
    { key: 'friday', label: 'الجمعة' },
    { key: 'saturday', label: 'السبت' }
  ];

  // هيكل بيانات جديد: كل فترة عبارة عن كائن
  const [schedule, setSchedule] = useState({
    sunday: { enabled: false, times: [] },
    monday: { enabled: false, times: [] },
    tuesday: { enabled: false, times: [] },
    wednesday: { enabled: false, times: [] },
    thursday: { enabled: false, times: [] },
    friday: { enabled: false, times: [] },
    saturday: { enabled: false, times: [] }
  });

  // ===================================================================
  // جلب الجدول الحالي
  // ===================================================================
  useEffect(() => {
    fetchCurrentSchedule();
  }, [entityId]);

  const fetchCurrentSchedule = async () => {
    try {
      const token = localStorage.getItem('EntityTokenA');
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/entity/profile`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();

        const parsed =
          typeof data.availability_schedule === 'string'
            ? JSON.parse(data.availability_schedule)
            : data.availability_schedule;

        if (parsed) {
          const newSchedule = {};
          daysOfWeek.forEach(day => {
            const oldTimes = parsed[day.key] || [];

            // تحويل المصفوفة القديمة ['09:00','17:00'] إلى [{from:'',to:''}]
            const converted = [];
            for (let i = 0; i < oldTimes.length; i += 2) {
              if (oldTimes[i] && oldTimes[i + 1]) {
                converted.push({
                  from: oldTimes[i],
                  to: oldTimes[i + 1]
                });
              }
            }

            newSchedule[day.key] = {
              enabled: converted.length > 0,
              times: converted
            };
          });

          setSchedule(newSchedule);
        }
      }
    } catch (err) {
      console.error('Error fetching schedule:', err);
      setError('حدث خطأ في جلب البيانات');
    } finally {
      setLoading(false);
    }
  };

  // ===================================================================
  // وظائف تعديل الجدول
  // ===================================================================

  const toggleDay = (dayKey) => {
    setSchedule(prev => ({
      ...prev,
      [dayKey]: {
        enabled: !prev[dayKey].enabled,
        times:
          !prev[dayKey].enabled && prev[dayKey].times.length === 0
            ? [{ from: '09:00', to: '17:00' }]
            : prev[dayKey].times
      }
    }));
  };

  const addTimeSlot = (dayKey) => {
    setSchedule(prev => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        times: [...prev[dayKey].times, { from: '09:00', to: '17:00' }]
      }
    }));
  };

  const removeTimeSlot = (dayKey, index) => {
    setSchedule(prev => {
      const updated = [...prev[dayKey].times];
      updated.splice(index, 1);
      return {
        ...prev,
        [dayKey]: { ...prev[dayKey], times: updated }
      };
    });
  };

  const updateTime = (dayKey, index, field, value) => {
    setSchedule(prev => {
      const updated = [...prev[dayKey].times];
      updated[index] = {
        ...updated[index],
        [field]: value
      };
      return {
        ...prev,
        [dayKey]: { ...prev[dayKey], times: updated }
      };
    });
  };

  const copyToAllDays = (sourceDayKey) => {
    const source = schedule[sourceDayKey];

    const newSchedule = {};
    daysOfWeek.forEach(day => {
      newSchedule[day.key] = {
        enabled: source.enabled,
        times: source.times.map(t => ({ ...t })) // deep copy
      };
    });

    setSchedule(newSchedule);
    setSuccessMessage('تم نسخ الأوقات لجميع الأيام');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  // ===================================================================
  // حفظ الجدول
  // ===================================================================
  const handleSave = async () => {
    setError('');
    setSuccessMessage('');
    setSaving(true);

    try {
      // تحقق من صحة البيانات
      for (const day of daysOfWeek) {
        if (schedule[day.key].enabled) {
          const times = schedule[day.key].times;

          if (times.length === 0) {
            setError(`يرجى إضافة أوقات لـ ${day.label}`);
            setSaving(false);
            return;
          }

          for (const t of times) {
            if (!t.from || !t.to) {
              setError(`يرجى إكمال الفترات في ${day.label}`);
              setSaving(false);
              return;
            }
            if (t.from >= t.to) {
              setError(`وقت النهاية يجب أن يكون بعد وقت البداية في ${day.label}`);
              setSaving(false);
              return;
            }
          }
        }
      }

      // تحويل الشكل الجديد إلى القديم للتوافق مع Backend
      const availabilitySchedule = {};
      daysOfWeek.forEach(day => {
        const times = schedule[day.key].enabled ? schedule[day.key].times : [];

        availabilitySchedule[day.key] = times.flatMap(t => [t.from, t.to]);
      });

      const token = localStorage.getItem('EntityTokenA');
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/EntityAvailability/availability`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ availability_schedule: availabilitySchedule })
        }
      );

      const data = await response.json();

      if (response.ok) {
        setSuccessMessage('تم حفظ أوقات العمل بنجاح');
        setTimeout(() => onSuccess && onSuccess(data), 1500);
      } else {
        setError(data.message || 'حدث خطأ في الحفظ');
      }
    } catch (err) {
      console.error('Error saving schedule:', err);
      setError('حدث خطأ في الاتصال بالخادم');
    } finally {
      setSaving(false);
    }
  };

  // ===================================================================
  // واجهة المستخدم
  // ===================================================================
  if (loading) {
    return (
      <div className="flex-center p-8">
        <div className="spinner"></div>
        <p className="text-white mt-4">جاري التحميل...</p>
      </div>
    );
  }

  return (
    <div className="availability-schedule">
      <h2 className="text-white text-2xl font-bold mb-6">⏰ إدارة أوقات العمل الأسبوعية</h2>

      <div className="glass p-6 mb-4">
        <p className="text-white opacity-80 text-sm mb-4">
          💡 حدد الأيام والأوقات المتاحة للحجز خلال الأسبوع. يمكنك إضافة عدة فترات زمنية لكل يوم.
        </p>
      </div>

      {daysOfWeek.map(day => (
        <div key={day.key} className="glass-dark p-4 rounded-lg mb-4">

          {/* عنوان اليوم */}
          <div className="flex-between mb-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={schedule[day.key].enabled}
                onChange={() => toggleDay(day.key)}
                className="w-5 h-5"
              />
              <span className="text-white font-bold text-lg">{day.label}</span>
            </label>

            {schedule[day.key].enabled && (
              <div className="flex gap-2">
                <button onClick={() => addTimeSlot(day.key)} className="btn btn-primary availability-schedule-margin-1b">
                  + إضافة فترة
                </button>
                <button onClick={() => copyToAllDays(day.key)} className="btn btn-secondary availability-schedule-margin-1b">
                  📋 نسخ لكل الأيام
                </button>
              </div>
            )}
          </div>

          {/* الفترات */}
          {schedule[day.key].enabled && (
            <div className="flex-column gap-3">
              {schedule[day.key].times.map((slot, index) => (
                <div key={index} className="flex items-center gap-3  availability-schedule-flex-dir-col">
                  <span className="text-white text-sm">من</span>
                  <input
                    type="time"
                    value={slot.from}
                    onChange={e => updateTime(day.key, index, 'from', e.target.value)}
                    className="input"
                  />

                  <span className="text-white text-sm">إلى</span>
                  <input
                    type="time"
                    value={slot.to}
                    onChange={e => updateTime(day.key, index, 'to', e.target.value)}
                    className="input"
                  />

                  <button
                    onClick={() => removeTimeSlot(day.key, index)}
                    className="btn btn-secondary"
                  >
                    🗑️ حذف
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {/* الرسائل */}
      {error && (
        <div className="bg-red-500 bg-opacity-20 border border-red-500 rounded-lg p-3">
          <p className="text-white text-sm">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-500 bg-opacity-20 border border-green-500 rounded-lg p-3">
          <p className="text-white text-sm">{successMessage}</p>
        </div>
      )}

      {/* الأزرار */}
      <div className="flex gap-4 mt-6">
        <button onClick={handleSave} className="btn btn-primary flex-1" disabled={saving}>
          {saving ? (
            <div className="flex-center gap-2">
              <div className="spinner"></div>
              <span>جاري الحفظ...</span>
            </div>
          ) : (
            '💾 حفظ التغييرات'
          )}
        </button>

        {onCancel && (
          <button onClick={onCancel} className="btn btn-secondary" disabled={saving}>
            إلغاء
          </button>
        )}
      </div>
    </div>
  );
};

export default AvailabilitySchedule;
